import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function WakefieldCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Wakefield Pothole Claims | 18% of U-Roads in RED | Fixtyer"
        description="Wakefield Council earns GREEN on spend but AMBER overall — 18% of its 1,070km of residential roads are in RED condition, up from 13.7% in 2020, with 40,905 potholes filled in five years. See what that means for your claim."
        canonical="/councils/wakefield"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Wakefield', url: '/councils/wakefield' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Wakefield has a GREEN spend scorecard — can I still claim for pothole damage?',
            answer: 'Yes. The DfT Spend scorecard is GREEN because Wakefield consistently spends above its DfT capital allocation and targets 92% preventative maintenance in 2025/26. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired — not on aggregate spend. Wakefield\'s own condition data shows RED A-roads nearly doubled since 2020, B/C roads in RED up from 2.6% to 4.3%, and U-road RED condition rising from 13.7% to 18%.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'U-roads make up 1,070km — 72% of Wakefield\'s 1,484km carriageway network. The council only publishes RED-condition percentages for U-roads, not amber or green breakdowns. At the latest survey, 18% of U-roads were in RED condition — roughly 193km of estate roads, village routes and residential streets. The council\'s own strategy admits its aim is to "deliver improvements to the unclassified roads" because classified roads are in "already good condition."',
          },
          {
            question: 'Why does Wakefield only report RED percentages for U-roads?',
            answer: 'Wakefield\'s transparency report condition tables show full red/amber/green breakdowns for A-roads and B/C roads, but U-roads list only the RED percentage with amber and green marked N/A for every year from 2020 to 2024. That means you cannot see what share of the 72% residential network is in amber (maintenance may be required soon) — only that nearly one in five U-roads has reached RED (should be considered for maintenance).',
          },
          {
            question: 'Does Wakefield using claims history in road prioritisation help my claim?',
            answer: 'It cuts both ways. Wakefield\'s prioritisation methodology explicitly includes "claims history (have compensation claims been submitted)" alongside pothole repair counts and area technician surveys. If you have already claimed for the same road, the council may have documented notice. If you have not, the absence of a prior claim on a road with heavy reactive repair history strengthens the argument the defect was not proactively addressed.',
          },
          {
            question: 'Wakefield filled 9,086 potholes in 2024/25 — does that mean the roads are fixed?',
            answer: 'No. Wakefield filled 9,086 potholes in 2024/25 — down from a five-year peak of 11,004 in 2023/24, but still 40,905 over five years. The council states that "over the last 5 years, we have repaired thousands of potholes, which eliminates the risk of accidents and damage to vehicles" — yet U-road RED condition rose from 13.7% to 18% in the same period. Reactive volume and worsening survey data can coexist.',
          },
          {
            question: 'How does Section 58 apply to Wakefield\'s risk-based inspection regime?',
            answer: 'Section 58 of the Highways Act 1980 lets councils defend claims by proving reasonable maintenance systems. Wakefield follows the Well Managed Highway Infrastructure Code with risk-based inspections, 4-hour to 28-day repair targets, and AI-assisted surveys. The council itself states that "demonstrating that the Council maintains the public highway in accordance with the Code is essential to be able to counter such claims." Your claim succeeds when evidence shows the specific defect met intervention criteria, was previously reported, or should have been found before you hit it.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Wakefield Council"
        councilSlug="wakefield"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/wakefield"
        headline="Wakefield Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Wakefield</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Wakefield: U-Road RED Up to 18% Despite GREEN Spend
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Wakefield Council spends above its DfT allocation and earns a <strong className="text-emerald-400">GREEN</strong> spend
            scorecard — yet the overall rating is <strong className="text-amber-400">AMBER</strong> because road condition is
            slipping. Their own transparency report shows <strong className="text-rose-400">18% of U-roads in RED condition</strong> —
            up from 13.7% in 2020 — on a network where 72% of carriageway is residential, and{' '}
            <strong className="text-rose-400">40,905 potholes filled in five years</strong>.
          </p>

          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">18%</div>
            <div className="text-white font-medium mt-2">U-roads in RED condition (2024)</div>
            <div className="text-brand-300 text-sm mt-1">
              Up from 13.7% in 2020 — roughly 193km of estate and residential roads on Wakefield's
              1,070km unclassified network. The council publishes only RED percentages for U-roads, not amber or green.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of survey data from Wakefield's own transparency report — classified roads
            deteriorating, U-road RED rising, and a partial picture on 72% of the network
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (196km — 13.2% of network): RED nearly doubled</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">1.7%</td>
                    <td className="px-4 py-2 text-center">21.1%</td>
                    <td className="px-4 py-2 text-center">77.2%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">2%</td>
                    <td className="px-4 py-2 text-center">20%</td>
                    <td className="px-4 py-2 text-center">78%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">2.5%</td>
                    <td className="px-4 py-2 text-center">24.7%</td>
                    <td className="px-4 py-2 text-center">72.8%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">3.1%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">26%</td>
                    <td className="px-4 py-2 text-center font-bold">70.9%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              RED A-roads rose from 1.7% to 3.1% — an 82% increase. Amber climbed from 21.1% to 26%,
              meaning nearly three in ten main roads may need maintenance soon.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (218km — 14.7% of network): RED up 65%</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">2.6%</td>
                    <td className="px-4 py-2 text-center">22.2%</td>
                    <td className="px-4 py-2 text-center">75.2%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">1.6%</td>
                    <td className="px-4 py-2 text-center">17.4%</td>
                    <td className="px-4 py-2 text-center">76.87%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">3.42%</td>
                    <td className="px-4 py-2 text-center">24.37%</td>
                    <td className="px-4 py-2 text-center">72.21%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">4.3%</td>
                    <td className="px-4 py-2 text-center font-bold">26.6%</td>
                    <td className="px-4 py-2 text-center font-bold">69.1%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              RED B/C roads rose from 2.6% in 2020 to 4.3% in 2024. Combined amber and red now account
              for 30.9% of the B/C network — nearly one in three local classified roads needs attention.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">And This Is The Well-Funded Version</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£12.6m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£16.1m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">92%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share 2025/26</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Wakefield spends <span className="text-emerald-400">28% above its DfT allocation</span> in
              2025/26 and targets 92% preventative maintenance — yet U-road RED condition still rose from
              13.7% to 18% over five years. The problem is not the chequebook alone. The residential network
              is deteriorating faster than planned treatments are catching up.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road Blind Spot */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 1,070km Residential Majority
          </h2>
          <p className="text-surface-600 mb-8">
            72% of Wakefield's carriageway is unclassified roads — and the council only publishes RED percentages
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Amber / Green</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">13.7%</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">Not published</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">13.8%</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">Not published</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">12.3%</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">Not published</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">12.2%</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">Not published</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">18%</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">Not published</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The Partial Picture Problem</h3>
              <p className="text-surface-600 text-sm mb-3">
                Wakefield's condition tables give full red/amber/green breakdowns for A-roads and B/C roads.
                For U-roads, amber and green are marked N/A every year. You can see RED rising to 18% — but
                not how much of the remaining 82% is genuinely green versus amber.
              </p>
              <p className="text-surface-600 text-sm">
                At 18% RED on 1,070km, approximately <strong>193km of residential and estate roads</strong> should
                be considered for maintenance under the council's own RED definition.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Council's Own Priority</h3>
              <p className="text-amber-800 text-sm mb-3">
                Wakefield states its highway strategy aims to "deliver improvements to the unclassified roads,
                whilst maintaining the already good condition of the classified roads." That admission frames
                U-roads as the problem area — yet RED U-road condition worsened from 13.7% to 18% while
                classified RED also climbed.
              </p>
              <p className="text-amber-800 text-sm">
                Road prioritisation explicitly weighs "claims history (have compensation claims been submitted)"
                and "level of reactive repair intervention (number of pothole repairs carried out)."
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for
              knowing the condition of its roads. For Wakefield's unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last condition-surveyed — and what did the survey record?</li>
              <li>• If 18% of U-roads are RED, what proactive work was scheduled for your street?</li>
              <li>• Does the council's own prioritisation methodology show prior claims or pothole repairs on your road?</li>
              <li>• Why is amber/green condition data absent for 72% of the network you are most likely to drive on?</li>
            </ul>
            <p className="text-white font-medium">
              A council that tracks claims history for resurfacing decisions cannot claim ignorance of
              roads where compensation has already been paid.
            </p>
          </div>
        </div>
      </section>

      {/* The Repair Conveyor Belt */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            40,905 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            The scale of reactive repair tells you how many defects this network produces
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">6,396</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">7,717</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">7,702</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">11,004</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">9,086</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">40,905</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~22 Potholes a Day, Every Day</h3>
              <p className="text-surface-600 text-sm">
                Averaged over five years, Wakefield fills around 22 potholes per day across its 1,484km
                network. 2023/24 was the peak at 11,004 — roughly 30 per day. A network producing defects
                at that rate is one where potholes routinely form between inspections.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Inspection and Repair Targets</h3>
              <p className="text-amber-800 text-sm">
                Wakefield's area technicians carry out risk assessments on identified defects, with repair
                targets of 4-hour, 24-hour, 7-day, or 28-day depending on severity. Inspection frequency
                is determined by road classification and traffic volume. Your claim turns on whether your
                defect met the intervention threshold and was left beyond its repair window.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Spending detail */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Five Years of Highway Spending
          </h2>
          <p className="text-surface-600 mb-8">
            CRSTS funding via West Yorkshire Mayoral Combined Authority — capital consistently above DfT allocation
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT allocation</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Capital spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-emerald-700">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-rose-700">Reactive</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">£6.97m</td>
                  <td className="px-4 py-3 text-center">£5.92m</td>
                  <td className="px-4 py-3 text-center">£2.75m</td>
                  <td className="px-4 py-3 text-center">68%</td>
                  <td className="px-4 py-3 text-center">32%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">£7.30m</td>
                  <td className="px-4 py-3 text-center">£8.26m</td>
                  <td className="px-4 py-3 text-center">£2.17m</td>
                  <td className="px-4 py-3 text-center">79%</td>
                  <td className="px-4 py-3 text-center">21%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">£9.16m</td>
                  <td className="px-4 py-3 text-center">£10.98m</td>
                  <td className="px-4 py-3 text-center">£2.81m</td>
                  <td className="px-4 py-3 text-center">80%</td>
                  <td className="px-4 py-3 text-center">20%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">£9.61m</td>
                  <td className="px-4 py-3 text-center">£10.67m</td>
                  <td className="px-4 py-3 text-center">£2.54m</td>
                  <td className="px-4 py-3 text-center">81%</td>
                  <td className="px-4 py-3 text-center">19%</td>
                </tr>
                <tr className="bg-emerald-50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26</td>
                  <td className="px-4 py-3 text-center">£12.56m</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-700">£16.14m</td>
                  <td className="px-4 py-3 text-center">£1.33m</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-700">92%</td>
                  <td className="px-4 py-3 text-center">8%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Reactive Repairs — Revenue funding covers a wide range of routine maintenance works on the highway. This includes masonry repairs, flagging, kerbing, patching, and responding to emergency incidents occurring outside the normal working hours, such as attending road traffic collisions, clearing debris, and winter sanding. These costs go well beyond just pothole repairs."
              source="Wakefield Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Council Top-Up Funding</h3>
              <p className="text-surface-600 text-sm">
                Beyond CRSTS settlement funding, Wakefield annually allocates its own budget for highway
                maintenance — vegetation management, drainage upgrades, traffic sign cleaning, and
                guardrail replacement. The council notes this "plays a key role in the overall management
                of highway assets."
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">2025/26 Planned Works</h3>
              <p className="text-surface-600 text-sm">
                Wakefield plans 5.4km of carriageway resurfacing and 39km of preventative treatments in
                2025/26, plus 150 footways already treated via slurry seal with more planned. Large schemes
                include the Castleford Growth Corridor (£13.49m) and Pontefract Streets for People (£3.5m).
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Section 58 Admission */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Section 58 Admission
          </h2>
          <p className="text-surface-600 mb-8">
            The council's own framing of claims defence — in its own words
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="An inadequately maintained network can present a danger to highway users, create congestion and pollution through unplanned road works, cause a detrimental impact on the local economy, and lead to an increase in 'third-party' claims against the Council for vehicle damage and personal injury."
              source="Wakefield Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="As the Highway Authority, the Council has certain legal obligations it must meet to ensure the highway network is safe and fit for purpose. From time to time, these obligations may become the subject of claims for loss or personal injury. Demonstrating that the Council maintains the public highway in accordance with the Code is essential to be able to counter such claims and protect the 'public purse'."
              source="Wakefield Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="The aim of the highway services is to deliver improvements to the unclassified roads, whilst maintaining the already good condition of the classified roads."
              source="Wakefield Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">AI and 360-Degree Surveys</h3>
              <p className="text-surface-600 text-sm mb-3">
                Wakefield has added AI-assisted condition monitoring and a full-network 360 imagery survey
                capturing carriageway images every 1 metre and 35 defect types. The council states this
                allows it to "maximise the potential of using preventative treatment" by cross-examining
                data across years.
              </p>
              <p className="text-surface-600 text-sm">
                Enhanced survey capability strengthens the council's Section 58 argument — but also means
                defects visible on survey imagery that were not repaired are harder to excuse.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Questions Worth Asking</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• Was your road captured in the 360 survey — and was the defect visible?</li>
                <li>• Does Wakefield's prioritisation record show prior pothole repairs on your street?</li>
                <li>• If claims history feeds resurfacing decisions, has anyone claimed here before?</li>
                <li>• Did the defect exceed the council's intervention threshold before you hit it?</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* What AMBER + GREEN spend means for claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Well-Funded AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Wakefield invests seriously — here's how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — capital exceeds DfT allocation every year since 2022/23
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 92% preventative maintenance targeted in 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Well Managed Highway Infrastructure Code with risk-based inspections
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> AI-assisted surveys and full-network 360 imagery capture
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented 4-hour to 28-day repair response targets
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a structured Section 58 defence. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — U-road RED up from 13.7% to 18% since 2020
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 72% of network is U-roads with only RED data published
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> B/C RED rose from 2.6% to 4.3%; A-road RED from 1.7% to 3.1%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 40,905 potholes filled in five years — peak 11,004 in 2023/24
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Claims history explicitly feeds road prioritisation decisions
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits unclassified roads need improvement while RED worsens
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and documented inspection regimes, your claim lives or dies on
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on a U-road, the partial condition data and 18% RED rate are your structural arguments</li>
              <li>• Whether the defect exceeded Wakefield's intervention threshold and sat beyond its repair window</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Wakefield's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Wakefield?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road RED trend argument</li>
                <li>• No prior-report search</li>
                <li>• No claims-history prioritisation angle</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 18% U-road RED condition documented</li>
                <li>✅ 40,905 potholes in five years cited</li>
                <li>✅ Partial U-road survey data argued</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Wakefield</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=wakefield"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Wakefield Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Wakefield has a GREEN spend scorecard — can I still claim for pothole damage?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN because Wakefield consistently spends above its DfT
                capital allocation and targets 92% preventative maintenance in 2025/26. Section 58 turns on
                whether the specific defect that damaged your vehicle was reasonably inspected and repaired —
                not on aggregate spend. Wakefield's own condition data shows RED A-roads nearly doubled since
                2020, B/C roads in RED up from 2.6% to 4.3%, and U-road RED condition rising from 13.7% to 18%.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 1,070km — 72% of Wakefield's 1,484km carriageway network. The council only
                publishes RED-condition percentages for U-roads, not amber or green breakdowns. At the latest
                survey, 18% of U-roads were in RED condition — roughly 193km of estate roads, village routes
                and residential streets. The council's own strategy admits its aim is to "deliver improvements
                to the unclassified roads" because classified roads are in "already good condition."
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why does Wakefield only report RED percentages for U-roads?
              </h3>
              <p className="text-surface-600 text-sm">
                Wakefield's transparency report condition tables show full red/amber/green breakdowns for A-roads
                and B/C roads, but U-roads list only the RED percentage with amber and green marked N/A for every
                year from 2020 to 2024. That means you cannot see what share of the 72% residential network is
                in amber (maintenance may be required soon) — only that nearly one in five U-roads has reached
                RED (should be considered for maintenance).
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Wakefield using claims history in road prioritisation help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It cuts both ways. Wakefield's prioritisation methodology explicitly includes "claims history
                (have compensation claims been submitted)" alongside pothole repair counts and area technician
                surveys. If you have already claimed for the same road, the council may have documented notice.
                If you have not, the absence of a prior claim on a road with heavy reactive repair history
                strengthens the argument the defect was not proactively addressed.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Wakefield filled 9,086 potholes in 2024/25 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Wakefield filled 9,086 potholes in 2024/25 — down from a five-year peak of 11,004 in
                2023/24, but still 40,905 over five years. The council states that "over the last 5 years, we
                have repaired thousands of potholes, which eliminates the risk of accidents and damage to
                vehicles" — yet U-road RED condition rose from 13.7% to 18% in the same period. Reactive
                volume and worsening survey data can coexist.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                How does Section 58 apply to Wakefield's risk-based inspection regime?
              </h3>
              <p className="text-surface-600 text-sm">
                Section 58 of the Highways Act 1980 lets councils defend claims by proving reasonable
                maintenance systems. Wakefield follows the Well Managed Highway Infrastructure Code with
                risk-based inspections, 4-hour to 28-day repair targets, and AI-assisted surveys. The council
                itself states that "demonstrating that the Council maintains the public highway in accordance
                with the Code is essential to be able to counter such claims." Your claim succeeds when evidence
                shows the specific defect met intervention criteria, was previously reported, or should have been
                found before you hit it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.wakefield.gov.uk/roads-and-travel/roads/highways-asset-management/highways-asset-management-funding"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Wakefield Council Local Highways Maintenance Transparency Report 2025
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
